import {
  classes,
  getCurrentOwnerSub,
  horses,
  isHeldOp,
  isOwnerless,
  people,
  rides,
  shows,
} from "./db";
import { rideCnoNumber } from "../util/rideDisplay";

// A normal (non-manual) ride carries only horseId/riderId FKs, resolved
// against the same offline caches useScoreSheet.js's fromCache()/fromApi()
// join against (horsesCache/peopleCache) — the cached ride object itself
// never carries horseName/riderName as fields. A manual ride (store/
// manualRides.js) is the one exception: it carries free-text horseName/
// riderName directly, with no horse/rider id to look up.
function riderDisplayName(person) {
  if (!person) return null;
  const name = [person.firstName, person.lastName].filter(Boolean).join(" ");
  return name || null;
}

// #1921 — human-readable descriptions of outbox ops for the sync-status
// detail view (SyncStatus.jsx) and the outbox export. The outbox itself
// only carries ids in op.payload; this resolves them against the cached
// entity stores (read-only — hydration is what keeps those fresh, nothing
// here ever writes) so the detail view can name a ride instead of a rideId.
//
// Card #1923 (owner-tagging outbox ops on enqueue/sign-in) plans to route
// its "you inherited someone else's queued work" message into this same
// detail view — the descriptor shape here is generic (op + resolved
// entities), not hardcoded to the #1562 403 case, so that second caller has
// somewhere to plug in.

function resolveRideId(op) {
  return op.payload?.rideId ?? op.tempId ?? null;
}

// Resolves one outbox op against the cached entity stores. Never throws: a
// ride/class/show that isn't cached (e.g. cache wiped between saves) simply
// comes back null, and the caller falls back to showing the raw id.
export async function describeOutboxOp(op) {
  const rideId = resolveRideId(op);
  const ride = rideId ? await rides.get(rideId) : null;
  const klass = ride?.classId ? await classes.get(ride.classId) : null;
  const show = klass?.showId ? await shows.get(klass.showId) : null;
  const horse =
    ride && !ride.isManual && ride.horseId
      ? await horses.get(ride.horseId)
      : null;
  const person =
    ride && !ride.isManual && ride.riderId
      ? await people.get(ride.riderId)
      : null;
  return {
    opId: op.opId,
    type: op.type,
    status: op.status,
    attempts: op.attempts ?? 0,
    errorClass: op.errorClass ?? null,
    httpStatus: op.httpStatus ?? null,
    lastError: op.lastError ?? null,
    // #2111 AC5 — the interceptor-captured backend detail behind the generic
    // lastError message (see sync.js's flush() catch block), for the closed-
    // by-default raw error-detail expander. null (never undefined) when
    // nothing was ever captured, e.g. a pre-#2111 legacy row.
    rawErrorDetail: op.rawErrorDetail ?? null,
    // #2726 — the two pieces of the server's JSend fail body that
    // installServerErrorInterceptor discards on its way to rawErrorDetail
    // (see sync.js's flush() catch): the machine-readable failure code and
    // the field the server refused. Carried through so the expander can say
    // WHICH field was rejected, not just that something 409'd. null (never
    // undefined) for a pre-#2726 row or a failure with no JSend body.
    failureCode: op.failureCode ?? null,
    failureField: op.failureField ?? null,
    // #1923 — the sub that stamped this op at enqueue time (null for a
    // pre-#1923 legacy row with no owner). Carried through so the detail
    // view can distinguish "held, belongs to someone else" from the other
    // surfaced states without re-deriving it from raw db rows.
    owner: op.owner ?? null,
    // #2031 AC3 — carried straight through from sync.js's listSurfacedOps
    // (which computes it fresh against the FULL outbox, something a single
    // op alone can't answer); false/undefined for any op reached some other
    // way (e.g. a raw fixture in a test) rather than ever throwing.
    blockedOnStuckRide: op.blockedOnStuckRide ?? false,
    judgePosition: op.payload?.judgeId ?? null,
    rideId,
    competitorNumber: ride ? (rideCnoNumber(ride) ?? null) : null,
    horseName: ride?.isManual ? (ride.horseName ?? null) : (horse?.name ?? null),
    riderName: ride?.isManual
      ? (ride.riderName ?? null)
      : riderDisplayName(person),
    classNumber: klass?.classNumber ?? null,
    className: klass?.className ?? null,
    showName: show?.name ?? null,
    // #2111 AC3 — the ride's own scheduled time (same field RideList.jsx
    // renders via formatVenueTime), so a scribe staring at a stuck item can
    // tell WHICH ride on the sheet this is without leaving the dialog to go
    // looking for it in the running order.
    rideScheduledTime: ride?.scheduledTime ?? null,
  };
}

export async function describeOutboxOps(ops) {
  return Promise.all(ops.map(describeOutboxOp));
}

// #885/#1562 — the one status the detail view explains in plain words
// instead of a bare code: the per-sheet scribe-assignment check. The signed-
// in user is not in the class's scribeAssignments for this judge position;
// never fixed by renewing, signing out, or signing back in as someone else
// who IS assigned. A boolean rather than a returned i18n key, so the caller
// always calls t() with a static literal (i18n keys must be static
// literals — see the caller).
export function isNotAssignedScribeError(op) {
  return op.httpStatus === 403;
}

// #1923 / #2031 (judge finding on #1923, fixed here) - this USED to read
// purely off op.status ("a pending row in the surfaced list can only be a
// held op"), which held for the ORDINARY held case but broke for a held op
// that ALSO carries status "error": one attempted under its real owner,
// before that owner got swapped out (sync.js's #1923 F4 comment). That op
// reads as held on the banner (heldSync/heldCount counts it) but this
// predicate said false for it - the row then showed attempts + an error
// class with no held explanation, directly contradicting the banner above
// it. Now reads real ownership (op.owner vs the current sub) instead of
// guessing from status, mirroring sync.js's isHeldForCurrentOwner exactly
// (including the terminal exclusion: a held op that's ALSO already terminal
// keeps the terminal/403 treatment - see that function's own comment for
// why). Exported so SyncStatus.jsx's per-row explanation doesn't re-derive
// the invariant inline, and so it is independently testable.
export function isHeldForDisplay(op) {
  return op.status !== "terminal" && isHeldOp(op, getCurrentOwnerSub());
}

// #2031 AC2 - the hard boundary the Claim affordance is gated on: a row only
// ever offers Claim when it is BOTH held (per isHeldForDisplay above) AND
// carries no owner at all. isOwnerless alone would not be a safe UI gate on
// its own if some future caller ever handed this a non-held-but-ownerless
// shape; requiring both keeps the check explicit rather than assumed.
export function isClaimable(op) {
  return isHeldForDisplay(op) && isOwnerless(op);
}

// A short label naming the ride a Discard action would affect, for the
// confirm dialog — "the confirm names the ride and what is being thrown
// away" (#1921 AC). Falls back to the raw ride id, then to a plainly
// "unidentified" label, rather than ever showing nothing.
export function describeRideForDiscard(descriptor) {
  if (descriptor.competitorNumber) {
    return descriptor.horseName
      ? `#${descriptor.competitorNumber} — ${descriptor.horseName}`
      : `#${descriptor.competitorNumber}`;
  }
  if (descriptor.rideId) return `ride ${descriptor.rideId}`;
  return "an unidentified ride";
}
